/* eslint-disable no-useless-catch */
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
} from "firebase/auth";
import { collection, doc, getDoc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "@/firebase";
import { OrgAccess, User } from "@/types";
import { Access } from "@/types";
import { pick } from "lodash";
import { RootState } from "@/store/store";

interface AuthState {
  user: User | null;
}

const initialState: AuthState = {
  user: null,
};

export const register = createAsyncThunk(
  "auth/signUp",
  async (payload: { email: string; password: string; name: string; lastname: string }) => {
    try {
      // Create user in auth
      const userCredential = await createUserWithEmailAndPassword(auth, payload.email, payload.password);
      const user = userCredential.user;

      // Verify user email
      await sendEmailVerification(user);

      // Create user in firestore
      const docRef = doc(db, `users/${user.uid}`);

      const ALLOWED_FIELDS = ["name", "lastname", "email"];

      const newUser = await setDoc(docRef, pick(payload, ALLOWED_FIELDS));

      // Return user info
      return newUser;
    } catch (error) {
      throw error;
    }
  }
);

export const getProfile = createAsyncThunk("auth/getProfile", async (uid: string) => {
  try {
    const docRef = doc(db, `users/${uid}`);
    const user = await getDoc(docRef);

    const accessRef = collection(db, `users/${uid}/access`);
    const access = await getDocs(accessRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc) => {
        return { ...acc, [doc.id]: { ...doc.data() } };
      }, [] as OrgAccess[]);
    });

    return {
      name: user.data()?.name,
      lastname: user.data()?.lastname,
      access,
    };
  } catch (error) {
    throw error;
  }
});

interface LogInForm {
  email: string;
  password: string;
}

export const login = createAsyncThunk("auth/login", async ({ email, password }: LogInForm, { dispatch }) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    dispatch(getProfile(userCredential.user.uid));

    return {
      id: userCredential.user.uid,
      email: userCredential.user.email,
      emailVerified: userCredential.user.emailVerified,
    };
  } catch (error) {
    throw error;
  }
});

export const loginAnonymously = createAsyncThunk("auth/loginAnonymously", async (_, { dispatch }) => {
  try {
    const userCredential = await signInAnonymously(auth);

    dispatch(getProfile(userCredential.user.uid));

    return {
      id: userCredential.user.uid,
      email: userCredential.user.email,
      emailVerified: userCredential.user.emailVerified,
    };
  } catch (error) {
    throw error;
  }
});

export const updateUser = createAsyncThunk("auth/updateUser", async (user: Partial<User>, { getState }) => {
  const store = getState() as RootState;

  try {
    const ALLOWED_FIELDS = ["name", "lastname", "email", "phone", "locations"];

    const payload = {
      ...pick(user, ALLOWED_FIELDS),
      lastUpdatedAt: Date.now(),
      lastUpdatedBy: store.auth?.user?.uid,
    };
    const docRef = doc(db, `users/${user.uid}`);

    await updateDoc(docRef, payload);

    return user;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const passwordReset = createAsyncThunk("auth/passwordReset", async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const logOut = createAsyncThunk("auth/logOut", async () => {
  try {
    await signOut(auth);
  } catch (err) {
    return err;
  }
});

export const userSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => ({
      ...state,
      user: action.payload,
    }),
    addPersonalInfo: (state: AuthState, action: PayloadAction<User>) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    updatePersonalInfo: (state: AuthState, action: PayloadAction<User>) => {
      if (!action.payload) return state;
      state.user = {
        ...state.user,
        location: {
          ...state.user?.location,
          ...action.payload.location, // Only update the location object
        },
      };
    },
    setUserAccess: (state: AuthState, action: PayloadAction<(Access & { id: string })[]>) => {
      const accessList = action.payload;

      const accessObject = accessList.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.id]: curr,
        };
      }, {} as Record<string, OrgAccess>);

      return {
        ...state,
        user: {
          ...state.user,
          access: accessObject,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProfile.fulfilled, (state, action: PayloadAction<User | undefined>) => {
      if (!action.payload) return state;
      return {
        ...state,
        user: {
          ...state.user,
          name: action?.payload.name,
          lastname: action?.payload.lastname,
          access: action?.payload.access,
        },
      };
    });
    builder.addCase(register.fulfilled, (state, action: unknown) => {
      return {
        ...state,
        user: action?.payload,
      };
    });
    builder.addCase(register.rejected, (state, action: unknown) => {
      if (action?.error.code === "auth/user-not-found") {
        alert("Usuario no encontrado");
      }
      return state;
    });
    builder.addCase(login.fulfilled, (state, action: unknown) => {
      return {
        ...state,
        user: {
          id: action?.payload.uid,
          email: action?.payload.email,
          emailVerified: action?.payload.emailVerified,
          name: action?.payload.name,
          lastname: action?.payload.lastname,
        },
      };
    });
    builder.addCase(loginAnonymously.fulfilled, (state, action: unknown) => {
      return {
        ...state,
        user: {
          id: action?.payload.uid,
        },
      };
    });
    //
    builder.addCase(updateUser.fulfilled, (state, action: PayloadAction<User | undefined>) => {
      if (!action.payload) return state;

      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload,
        },
      };
    });

    builder.addCase(login.rejected, (state, action: unknown) => {
      if (action?.error.code === "auth/user-not-found") {
        alert("Usuario no encontrado");
      }
      return state;
    });
    builder.addCase(logOut.fulfilled, () => {
      return initialState;
    });
  },
});

export const { setUser, addPersonalInfo, updatePersonalInfo, setUserAccess } = userSlice.actions;
export const userSelector = (state: { auth: AuthState }) => state.auth.user;
export const isLoggedInSelector = createSelector([userSelector], (user) => !!user && user.emailVerified);
export default userSlice.reducer;
